import { useState, useCallback } from "react";
import { apiClient } from "../../utils/api";

interface AgentSharedResult {
  id: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  agent_id: string;
  input_values: string;
  binary_values: string;
  output_values: string;
}

interface AgentSharedApiResponse {
  data: AgentSharedResult;
  status: boolean;
}

interface UseAgentSharedReturn {
  getSharedResult: (resultSid: string) => Promise<AgentSharedResult | null>;
  loading: boolean;
  error: string;
  clearError: () => void;
}

const useAgentShared = (): UseAgentSharedReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const clearError = useCallback(() => setError(""), []);

  const getSharedResult = useCallback(
    async (resultSid: string): Promise<AgentSharedResult | null> => {
      setLoading(true);
      setError("");

      try {
        const res = await apiClient.get<AgentSharedApiResponse>(
          `/api/v1/agent/shared/result?result_sid=${resultSid}`
        );

        if (res.data.status === false) {
          throw new Error("공유된 결과를 불러올 수 없습니다.");
        }

        return res.data.data;
      } catch (err: any) {
        console.error("공유 결과 조회 실패:", err);
        let errorMessage = "공유된 결과를 불러올 수 없습니다.";

        if (err.response?.status === 404) {
          errorMessage = "존재하지 않는 공유 링크입니다.";
        } else if (err.message) {
          if (err.message.includes("timeout") || err.code === "ECONNABORTED") {
            errorMessage = "요청 시간이 초과되었습니다. 다시 시도해주세요.";
          } else if (err.message.includes("Network Error")) {
            errorMessage = "네트워크 연결을 확인하고 다시 시도해주세요.";
          } else {
            errorMessage = err.message;
          }
        }

        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    getSharedResult,
    loading,
    error,
    clearError,
  };
};

export default useAgentShared;
export type { AgentSharedResult, AgentSharedApiResponse, UseAgentSharedReturn }; 